import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { Swatch } from "@/components/primitives";
import { useAuth } from "@/hooks/useAuth";
import { useComments, type Comment } from "@/hooks/useComments";
import { useUserBlocks } from "@/hooks/useUserBlocks";
import { moderateComment } from "@/lib/commentModeration";

// Inline comments dropdown — expands/collapses within the feed card.
// Supports arbitrarily nested replies, likes, and pinning by the post owner.

// Visual indentation per depth level, capped so deep threads don't run off screen.
const INDENT_PX = 20;
const MAX_INDENT_DEPTH = 6;

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

interface Props {
  scanId: string | null;
  open: boolean;
  onDismiss?: () => void;
  onAdded?: (scanId: string) => void;
  onRemoved?: (scanId: string) => void;
}

interface RowProps {
  c: Comment;
  isOwner: boolean;
  isMine: boolean;
  depth: number;
  onReply: (c: Comment) => void;
  onLike: (id: string) => void;
  onPin: (id: string) => void;
  onRemove: (c: Comment) => void;
  onBlock: (c: Comment) => void;
}

function CommentRow({ c, isOwner, isMine, depth, onReply, onLike, onPin, onRemove, onBlock }: RowProps) {
  const indent = Math.min(depth, MAX_INDENT_DEPTH) * INDENT_PX;
  const small = depth > 0;
  return (
    <div style={{
      display: "flex", gap: 8, marginBottom: 10,
      paddingLeft: indent,
      borderLeft: depth > 0 ? "1px solid hsl(var(--sand-deep))" : undefined,
    }}>
      <Swatch
        kind={c.avatar}
        style={{
          width: small ? 20 : 24, height: small ? 20 : 24,
          borderRadius: "50%", border: "1px solid hsl(var(--ink))", flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
          <span style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 12,
            color: "hsl(var(--ink))",
          }}>
            {c.display_name || `@${c.handle}`}
          </span>
          <span className="caps" style={{ fontSize: 9 }}>· {timeAgo(c.created_at)}</span>
          {c.pinned && (
            <span className="caps" style={{
              fontSize: 9, color: "hsl(var(--rust))", border: "1px solid hsl(var(--rust))",
              padding: "0 4px", letterSpacing: "0.1em",
            }}>📌 pinned</span>
          )}
        </div>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
          color: "hsl(var(--ink))", lineHeight: 1.4, marginTop: 2,
          wordBreak: "break-word",
        }}>
          {c.body}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 4, alignItems: "center" }}>
          <button
            onClick={() => onLike(c.id)}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              color: c.liked_by_me ? "hsl(var(--rust))" : "hsl(var(--ink-2))",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
              display: "inline-flex", gap: 4, alignItems: "center",
            }}
          >
            {c.liked_by_me ? "♥" : "♡"} {c.like_count > 0 && <span>{c.like_count}</span>}
          </button>
          <button
            onClick={() => onReply(c)}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              color: "hsl(var(--ink-2))", fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
            }}
          >reply</button>
          {isOwner && depth === 0 && (
            <button
              onClick={() => onPin(c.id)}
              style={{
                background: "none", border: "none", padding: 0, cursor: "pointer",
                color: c.pinned ? "hsl(var(--rust))" : "hsl(var(--ink-2))",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
              }}
            >{c.pinned ? "unpin" : "pin"}</button>
          )}
          {isMine && (
            <button
              onClick={() => onRemove(c)}
              style={{
                background: "none", border: "none", padding: 0, cursor: "pointer",
                color: "hsl(var(--rust))", fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
                marginLeft: "auto",
              }}
            >delete</button>
          )}
          {!isMine && (
            <button
              onClick={() => onBlock(c)}
              style={{
                background: "none", border: "none", padding: 0, cursor: "pointer",
                color: "hsl(var(--ink-2))", fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
                marginLeft: "auto",
              }}
              title="Hide this user's notes from your feed"
            >block</button>
          )}
        </div>
      </div>
    </div>
  );
}

export function CommentsInline({ scanId, open, onDismiss, onAdded, onRemoved }: Props) {
  const { user } = useAuth();
  // Only load when the dropdown is open to avoid hammering the DB on the feed.
  const { comments, loading, posting, add, remove, toggleLike, togglePin, scanOwnerId } =
    useComments(open ? scanId : null);
  const { isBlocked, block } = useUserBlocks();
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState<Comment | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Synchronous guard against rapid double-submits: state updates from
  // `posting` are async, so a quick second click can slip through before
  // React re-renders. The ref flips immediately and blocks reentry.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) setTimeout(() => taRef.current?.focus({ preventScroll: true }), 50);
  }, [open]);

  // Collapse on Escape or on click/tap outside.
  useEffect(() => {
    if (!open || !onDismiss) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    const onClick = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      const target = e.target as Node | null;
      if (target && !root.contains(target)) onDismiss();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
    };
  }, [open, onDismiss]);

  // Group: pinned root → other roots (oldest→newest) → replies indexed by parent.
  // Blocked users' comments are stripped from the visible tree.
  const { roots, repliesByParent } = useMemo(() => {
    const roots: Comment[] = [];
    const repliesByParent = new Map<string, Comment[]>();
    const visible = comments.filter((c) => !isBlocked(c.user_id));
    for (const c of visible) {
      if (c.parent_id) {
        const list = repliesByParent.get(c.parent_id) ?? [];
        list.push(c);
        repliesByParent.set(c.parent_id, list);
      } else {
        roots.push(c);
      }
    }
    roots.sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return a.created_at.localeCompare(b.created_at);
    });
    return { roots, repliesByParent };
  }, [comments, isBlocked]);

  if (!open) return null;

  const isOwner = !!user && !!scanOwnerId && user.id === scanOwnerId;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || posting || submittingRef.current) return;
    submittingRef.current = true;
    const verdict = moderateComment(draft);
    if (!verdict.ok) {
      toast.error(verdict.reason ?? "This note can't be posted.");
      submittingRef.current = false;
      return;
    }
    try {
      const ok = await add(draft, replyTo?.id ?? null);
      if (ok) {
        setDraft("");
        setReplyTo(null);
        if (scanId) onAdded?.(scanId);
      }
    } finally {
      submittingRef.current = false;
    }
  };

  const handleRemove = async (c: Comment) => {
    await remove(c.id);
    if (scanId) onRemoved?.(scanId);
  };

  const handleBlock = async (c: Comment) => {
    if (!user) { toast.error("Sign in to block users"); return; }
    if (c.user_id === user.id) return;
    const label = c.display_name || `@${c.handle}`;
    if (typeof window !== "undefined" && !window.confirm(
      `Block ${label}? Their notes will be hidden from your feed.`
    )) return;
    await block(c.user_id, label);
  };

  const composer = (inline: boolean, depth = 1) => (
    <>
      {inline && replyTo && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "4px 8px", marginBottom: 4,
          marginLeft: Math.min(depth, MAX_INDENT_DEPTH) * INDENT_PX,
          background: "hsl(var(--cream))", border: "1px dashed hsl(var(--ink-2))",
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
          color: "hsl(var(--ink-2))", letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          <span>Replying to @{replyTo.handle}</span>
          <button
            onClick={() => setReplyTo(null)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "hsl(var(--rust))", padding: 0 }}
          >×</button>
        </div>
      )}
      <form onSubmit={submit} style={{
        display: "flex", gap: 6, alignItems: "flex-end",
        marginTop: 6,
        marginLeft: inline ? Math.min(depth, MAX_INDENT_DEPTH) * INDENT_PX : 0,
      }}>
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 500))}
          placeholder={user ? (inline && replyTo ? `Reply to @${replyTo.handle}…` : "Drop a note…") : "Sign in to leave a note"}
          disabled={!user || posting}
          rows={1}
          style={{
            flex: 1, resize: "none", minHeight: 32, maxHeight: 100,
            padding: "6px 8px",
            border: "1px solid hsl(var(--ink))", background: "hsl(var(--paper))",
            fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13, color: "hsl(var(--ink))",
            outline: "none",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void submit(e); }
            if (e.key === "Escape" && replyTo) { e.preventDefault(); setReplyTo(null); }
          }}
        />
        <button
          type="submit"
          disabled={!user || !draft.trim() || posting}
          style={{
            background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
            border: "none", padding: "8px 12px", cursor: "pointer",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
            letterSpacing: "0.12em", textTransform: "uppercase",
            opacity: !user || !draft.trim() || posting ? 0.5 : 1,
          }}
        >
          {posting ? "…" : "Post"}
        </button>
      </form>
    </>
  );

  return (
    <div ref={rootRef} style={{
      marginTop: 10,
      borderTop: "1px dashed hsl(var(--sand-deep))",
      paddingTop: 10,
    }}>
      {loading && (
        <div className="caps" style={{ textAlign: "center", padding: "12px 0", color: "hsl(var(--ink-2))", fontSize: 10 }}>
          loading notes…
        </div>
      )}
      {!loading && roots.length === 0 && (
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          color: "hsl(var(--ink-2))", textAlign: "center", padding: "10px 6px",
          fontSize: 13, lineHeight: 1.4,
        }}>
          No notes yet — be the first to drop one.
        </div>
      )}
      {roots.map((root) => {
        const renderNode = (node: Comment, depth: number): JSX.Element => {
          const children = (repliesByParent.get(node.id) ?? [])
            .slice()
            .sort((a, b) => a.created_at.localeCompare(b.created_at));
          return (
            <div key={node.id}>
              <CommentRow
                c={node}
                isOwner={isOwner}
                isMine={!!user && node.user_id === user.id}
                depth={depth}
                onReply={(c) => { setReplyTo(c); setTimeout(() => taRef.current?.focus(), 0); }}
                onLike={toggleLike}
                onPin={togglePin}
                onRemove={handleRemove}
                onBlock={handleBlock}
              />
              {children.map((child) => renderNode(child, depth + 1))}
              {replyTo && replyTo.id === node.id && composer(true, depth + 1)}
            </div>
          );
        };
        return (
          <div key={root.id} style={{
            marginBottom: 4,
            ...(root.pinned ? {
              background: "hsl(var(--cream))",
              border: "1px solid hsl(var(--sand-deep))",
              padding: "8px 8px 4px",
              marginBottom: 8,
            } : {}),
          }}>
            {renderNode(root, 0)}
          </div>
        );
      })}

      {!replyTo && composer(false, 0)}
    </div>
  );
}
